import { openDB, type IDBPDatabase } from 'idb'

/**
 * Offline file-list cache backed by IndexedDB.
 *
 * Goals:
 *   - Offline-first: when supabase fetch fails (no network), fall back to cache.
 *   - Stale-while-revalidate: useFileList shows cached data instantly, then
 *     refetches in the background and writes the fresh result.
 *   - TTL: cached entries older than 24h are treated as missing.
 *
 * Schema:
 *   db: dogdisk-offline (v1)
 *   store: file_list (key = parentId-or-"__root__")
 *     value = { rows: FileRow[], cached_at: number (ms epoch) }
 *
 * Notes:
 *   - We key root-level (parentId === null) under a sentinel `__root__` because
 *     IDB cannot key on null with autoIncrement off in our schema.
 *   - All errors are swallowed and logged via console.warn — cache is best-effort
 *     and must NEVER break the UI.
 */

// 后端计算列 thumb_url_list(file_info) 在 cover embed 内同名 select → cover 行的签名缩略图
export type ThumbCover = {
  name: string
  base_file_id: string | null
  thumb_url_list?: string | null
  thumb_url_feed?: string | null
} | null

export type FileRow = {
  id: string
  name: string
  type: string
  size?: number | null
  mime_type?: string | null
  status: string
  parent_id: string | null
  created_at: string
  // 缩略图相关（PostgREST select 补；旧缓存无此字段 → undefined，安全）
  base_file_id?: string | null
  cover?: ThumbCover
  // 后端签名缩略图列(PostgREST 计算列 thumb_url_list/feed(file_info));空 → 回退原图直链
  thumb_url_list?: string | null
  thumb_url_feed?: string | null
  // file_info.subtype:'pack_root' 标识资源包根文件夹(根列表置顶用);其余业务行为 null
  subtype?: string | null
}

const DB_NAME = 'dogdisk-offline'
const DB_VERSION = 1
const STORE_FILE_LIST = 'file_list'
const ROOT_KEY = '__root__'
const TTL_MS = 24 * 60 * 60 * 1000 // 24h

type CacheRecord = {
  rows: FileRow[]
  cached_at: number
}

let dbPromise: Promise<IDBPDatabase> | null = null

function db(): Promise<IDBPDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('indexedDB not available'))
  }
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_FILE_LIST)) {
          database.createObjectStore(STORE_FILE_LIST)
        }
      },
    })
  }
  return dbPromise
}

function keyFor(parentId: string | null): string {
  return parentId === null ? ROOT_KEY : parentId
}

/** Write the file list for a given parent into IndexedDB. */
export async function cacheFileList(
  parentId: string | null,
  rows: FileRow[],
): Promise<void> {
  try {
    const d = await db()
    const record: CacheRecord = { rows, cached_at: Date.now() }
    await d.put(STORE_FILE_LIST, record, keyFor(parentId))
  } catch (err) {
    console.warn('[offline-cache] cacheFileList failed:', err)
  }
}

/**
 * Read the cached file list. Returns null when:
 *   - IndexedDB unavailable (SSR / private mode)
 *   - No record for this parent
 *   - Record exists but is older than TTL_MS
 *   - Any IDB error occurs
 */
export async function getCachedFileList(
  parentId: string | null,
): Promise<FileRow[] | null> {
  try {
    const d = await db()
    const record = (await d.get(STORE_FILE_LIST, keyFor(parentId))) as
      | CacheRecord
      | undefined
    if (!record) return null
    if (Date.now() - record.cached_at > TTL_MS) return null
    return record.rows
  } catch (err) {
    console.warn('[offline-cache] getCachedFileList failed:', err)
    return null
  }
}

/** Clear all offline caches (used by sign-out flow). */
export async function clearOfflineCache(): Promise<void> {
  try {
    const d = await db()
    await d.clear(STORE_FILE_LIST)
  } catch (err) {
    console.warn('[offline-cache] clearOfflineCache failed:', err)
  }
}
